import React from "react";

export default function AIActionCenter({
  selectedClientId,
  reportStats,
  loading,
  accounts,
  handleGenerateAllDisputes,
  loadData,
  setError,
  setSuccessMessage,
}) {
  const disputeCandidates = Number(reportStats?.disputeCandidates || 0);
  const protectCount = Number(reportStats?.protect || 0);
  const hasSelectedClient = Boolean(selectedClientId);
  const hasAccounts = Array.isArray(accounts) && accounts.length > 0;

  async function handleReanalyze() {
    setError("");
    setSuccessMessage("");

    try {
      await loadData();
      setSuccessMessage("AI analysis refreshed successfully.");
    } catch (error) {
      setError(error?.message || "Unable to refresh AI analysis.");
    }
  }

  function handleReviewDisputes() {
    const accountSection = document.getElementById(
      "credit-report-accounts"
    );

    if (accountSection) {
      accountSection.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }

  function handleReviewProtectedAccounts() {
    const protectedAccounts = accounts.filter(
      (account) => account.protect_account === true
    );

    if (protectedAccounts.length === 0) {
      setError("");
      setSuccessMessage(
        "No protected positive accounts were found for this client."
      );
      return;
    }

    setError("");
    setSuccessMessage(
      `${protectedAccounts.length} positive account${
        protectedAccounts.length === 1 ? "" : "s"
      } should be protected from unnecessary disputes.`
    );

    handleReviewDisputes();
  }

  const cardBaseStyle = {
    borderRadius: "18px",
    padding: "20px",
    textAlign: "left",
    minHeight: "150px",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  };

  return (
    <div
      style={{
        background: "white",
        borderRadius: "22px",
        padding: "26px",
        border: "1px solid #e5e7eb",
        marginBottom: "28px",
        boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "16px",
          flexWrap: "wrap",
          marginBottom: "22px",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "28px",
              fontWeight: "900",
              color: "#0f172a",
              marginBottom: "6px",
            }}
          >
            🚀 AI Action Center
          </div>

          <div
            style={{
              fontSize: "15px",
              color: "#64748b",
              lineHeight: 1.6,
            }}
          >
            Take action using the AI recommendations for this credit
            profile.
          </div>
        </div>

        <div
          style={{
            background:
              disputeCandidates > 0 ? "#fef2f2" : "#f0fdf4",
            color: disputeCandidates > 0 ? "#b91c1c" : "#15803d",
            border:
              disputeCandidates > 0
                ? "1px solid #fecaca"
                : "1px solid #bbf7d0",
            borderRadius: "14px",
            padding: "10px 14px",
            fontSize: "14px",
            fontWeight: "800",
          }}
        >
          {disputeCandidates > 0
            ? `${disputeCandidates} actions recommended`
            : "No immediate disputes recommended"}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "16px",
        }}
      >
        <button
          type="button"
          onClick={() => {
            const bulkButton = document.getElementById(
              "generate-all-dispute-letters"
            );

            if (bulkButton) {
              bulkButton.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }}
          disabled={
            loading ||
            disputeCandidates === 0 ||
            !hasSelectedClient
          }
          style={{
            ...cardBaseStyle,
            border: "none",
            cursor:
              loading ||
              disputeCandidates === 0 ||
              !hasSelectedClient
                ? "not-allowed"
                : "pointer",
            background:
              loading ||
              disputeCandidates === 0 ||
              !hasSelectedClient
                ? "#e2e8f0"
                : "#D4AF37",
            color:
              loading ||
              disputeCandidates === 0 ||
              !hasSelectedClient
                ? "#64748b"
                : "#000000",
            boxShadow:
              loading ||
              disputeCandidates === 0 ||
              !hasSelectedClient
                ? "none"
                : "0 10px 20px rgba(37,99,235,0.25)",
          }}
        >
          <div
            style={{
              fontSize: "21px",
              fontWeight: "900",
              marginBottom: "8px",
            }}
          >
            ✉️ Generate Recommended Letters
          </div>

          <div
            style={{
              fontSize: "14px",
              lineHeight: 1.6,
              opacity: 0.9,
            }}
          >
            
{!hasSelectedClient
              ? "Select one client before generating dispute letters."
              : disputeCandidates === 0
              ? "No dispute candidates are currently available."
              : `Generate letters for ${disputeCandidates} recommended dispute accounts.`}
          </div>
        </button>

        <button
          type="button"
          onClick={handleReanalyze}
          disabled={loading}
          style={{
            ...cardBaseStyle,
            border: "1px solid #E6C85C",
            cursor: loading ? "not-allowed" : "pointer",
            background: loading ? "#e2e8f0" : "#FFF8E1",
            color: loading ? "#64748b" : "#A67C00",
          }}
        >
          <div
            style={{
              fontSize: "21px",
              fontWeight: "900",
              marginBottom: "8px",
            }}
          >
            🔄 Reanalyze Accounts
          </div>

          <div
            style={{
              fontSize: "14px",
              lineHeight: 1.6,
            }}
          >
            {loading
              ? "Refreshing account analysis..."
              : "Reload the accounts and retrieve the latest AI recommendations."}
          </div>
        </button>

        <button
          type="button"
          onClick={handleReviewDisputes}
          disabled={!hasAccounts}
          style={{
            ...cardBaseStyle,
            border: "1px solid #fecaca",
            cursor: !hasAccounts ? "not-allowed" : "pointer",
            background: !hasAccounts ? "#e2e8f0" : "#fef2f2",
            color: !hasAccounts ? "#64748b" : "#b91c1c",
          }}
        >
          <div
            style={{
              fontSize: "21px",
              fontWeight: "900",
              marginBottom: "8px",
            }}
          >
            🎯 Review Dispute Candidates
          </div>

          <div
            style={{
              fontSize: "14px",
              lineHeight: 1.6,
            }}
          >
            {!hasAccounts
              ? "No imported accounts are available."
              : `Review the ${disputeCandidates} accounts currently recommended for dispute.`}
          </div>
        </button>

        <button
          type="button"
          onClick={handleReviewProtectedAccounts}
          disabled={!hasAccounts || protectCount === 0}
          style={{
            ...cardBaseStyle,
            border: "1px solid #bbf7d0",
            cursor:
              !hasAccounts || protectCount === 0
                ? "not-allowed"
                : "pointer",
            background:
              !hasAccounts || protectCount === 0
                ? "#e2e8f0"
                : "#f0fdf4",
            color:
              !hasAccounts || protectCount === 0
                ? "#64748b"
                : "#15803d",
          }}
        >
          <div
            style={{
              fontSize: "21px",
              fontWeight: "900",
              marginBottom: "8px",
            }}
          >
            🛡️ Protect Positive Accounts
          </div>

          <div
            style={{
              fontSize: "14px",
              lineHeight: 1.6,
            }}
          >
            {protectCount === 0
              ? "No positive accounts are currently marked for protection."
              : `Review ${protectCount} positive account${
                  protectCount === 1 ? "" : "s"
                } that should not be disputed.`}
          </div>
        </button>

        <button
          type="button"
          disabled
          style={{
            ...cardBaseStyle,
            border: "1px solid #cbd5e1",
            cursor: "not-allowed",
            background: "#f8fafc",
            color: "#94a3b8",
          }}
        >
          <div
            style={{
              fontSize: "21px",
              fontWeight: "900",
              marginBottom: "8px",
            }}
          >
            📄 Export AI Analysis PDF
          </div>

          <div
            style={{
              fontSize: "14px",
              lineHeight: 1.6,
            }}
          >
            Coming next: export the executive summary and account
            recommendations as a professional PDF.
          </div>
        </button>

        <button
          type="button"
          disabled
          style={{
            ...cardBaseStyle,
            border: "1px solid #fde68a",
            cursor: "not-allowed",
            background: "#fffbeb",
            color: "#a16207",
            opacity: 0.7,
          }}
        >
          <div
            style={{
              fontSize: "21px",
              fontWeight: "900",
              marginBottom: "8px",
            }}
          >
            ⚖️ Build CFPB Complaint
          </div>

          <div
            style={{
              fontSize: "14px",
              lineHeight: 1.6,
            }}
          >
            Coming later: prepare a complaint workflow using verified
            dispute history and supporting evidence.
          </div>
        </button>
      </div>

      <div
        style={{
          marginTop: "18px",
          padding: "14px 16px",
          borderRadius: "14px",
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          color: "#64748b",
          fontSize: "14px",
          lineHeight: 1.6,
        }}
      >
        🛡️ Accounts marked for protection will not be intentionally
        targeted by the AI dispute workflow.
      </div>
    </div>
  );
}
